import { v4 as uuidv4 } from "uuid";
import {
  Group,
  GroupInvitation,
  GroupMember,
  GroupRole,
  GroupType,
  DeletedRecord,
  RecordShare,
  SharePermission,
  ShareRecordType,
} from "../../lib/types";
import { demoService } from "../demo/demoService";
import { API_URL, getAuthHeaders, handleApiError } from "./api";

type DemoGroupData = {
  groups: Group[];
  members: GroupMember[];
  invitations: GroupInvitation[];
  shares: RecordShare[];
};

const DEMO_GROUPS_STORAGE_KEY = "finance_demo_groups";
const DEMO_USER_ID = "demo-user";

const buildDemoData = (): DemoGroupData => {
  const now = new Date().toISOString();
  const groupA: Group = {
    id: uuidv4(),
    name: "Family Finances",
    type: "family",
    description: "Household budgets and shared goals.",
    ownerId: DEMO_USER_ID,
    createdBy: DEMO_USER_ID,
    createdAt: now,
    updatedAt: now,
    role: "owner",
  };
  const groupB: Group = {
    id: uuidv4(),
    name: "Studio Ops",
    type: "business",
    description: "Client projects and team cash flow.",
    ownerId: DEMO_USER_ID,
    createdBy: DEMO_USER_ID,
    createdAt: now,
    updatedAt: now,
    role: "admin",
  };

  const members: GroupMember[] = [
    {
      id: uuidv4(),
      groupId: groupA.id,
      userId: DEMO_USER_ID,
      role: "owner",
      status: "active",
      displayName: "You",
      email: "you@demo.local",
      invitedAt: now,
      acceptedAt: now,
    },
    {
      id: uuidv4(),
      groupId: groupA.id,
      userId: "demo-spouse",
      role: "editor",
      status: "active",
      displayName: "Jordan",
      email: "jordan@demo.local",
      invitedAt: now,
      acceptedAt: now,
    },
    {
      id: uuidv4(),
      groupId: groupB.id,
      userId: DEMO_USER_ID,
      role: "admin",
      status: "active",
      displayName: "You",
      email: "you@demo.local",
      invitedAt: now,
      acceptedAt: now,
    },
    {
      id: uuidv4(),
      groupId: groupB.id,
      userId: "demo-cfo",
      role: "viewer",
      status: "active",
      displayName: "Riley",
      email: "riley@demo.local",
      invitedAt: now,
      acceptedAt: now,
    },
  ];

  const invitations: GroupInvitation[] = [
    {
      id: uuidv4(),
      groupId: groupB.id,
      email: "casey@demo.local",
      role: "viewer",
      invitedBy: DEMO_USER_ID,
      invitedAt: now,
      expiresAt: new Date(Date.now() + 1000 * 60 * 60 * 24 * 7).toISOString(),
    },
  ];

  return {
    groups: [groupA, groupB],
    members,
    invitations,
    shares: [],
  };
};

const loadDemoData = (): DemoGroupData => {
  const stored = sessionStorage.getItem(DEMO_GROUPS_STORAGE_KEY);
  if (!stored) {
    const fresh = buildDemoData();
    sessionStorage.setItem(DEMO_GROUPS_STORAGE_KEY, JSON.stringify(fresh));
    return fresh;
  }
  return JSON.parse(stored) as DemoGroupData;
};

const saveDemoData = (data: DemoGroupData) => {
  sessionStorage.setItem(DEMO_GROUPS_STORAGE_KEY, JSON.stringify(data));
};

const demoGroupsAPI = {
  listGroups: async (): Promise<Group[]> => {
    const data = loadDemoData();
    return data.groups.map((group) => {
      const activeMembers = data.members.filter(
        (member) => member.groupId === group.id && member.status === "active"
      );
      const memberCount = activeMembers.length;
      const memberNames = activeMembers
        .map((m) => m.displayName || m.email || "Unknown")
        .filter(Boolean);
      return { ...group, memberCount, memberNames };
    });
  },
  getGroup: async (groupId: string): Promise<Group | null> => {
    const data = loadDemoData();
    const group = data.groups.find((item) => item.id === groupId);
    if (!group) {
      return null;
    }
    const memberCount = data.members.filter(
      (member) => member.groupId === group.id && member.status === "active"
    ).length;
    return { ...group, memberCount };
  },
  createGroup: async (
    payload: Pick<Group, "name" | "type" | "description">
  ): Promise<Group> => {
    const data = loadDemoData();
    const now = new Date().toISOString();
    const newGroup: Group = {
      id: uuidv4(),
      name: payload.name,
      type: payload.type,
      description: payload.description ?? null,
      ownerId: DEMO_USER_ID,
      createdBy: DEMO_USER_ID,
      createdAt: now,
      updatedAt: now,
      role: "owner",
    };
    data.groups.push(newGroup);
    data.members.push({
      id: uuidv4(),
      groupId: newGroup.id,
      userId: DEMO_USER_ID,
      role: "owner",
      status: "active",
      displayName: "You",
      email: "you@demo.local",
      invitedAt: now,
      acceptedAt: now,
    });
    saveDemoData(data);
    return newGroup;
  },
  updateGroup: async (
    groupId: string,
    payload: Partial<Pick<Group, "name" | "description" | "type">>
  ): Promise<Group> => {
    const data = loadDemoData();
    const groupIndex = data.groups.findIndex((item) => item.id === groupId);
    if (groupIndex === -1) {
      throw new Error("Group not found");
    }
    const updated = {
      ...data.groups[groupIndex],
      ...payload,
      updatedAt: new Date().toISOString(),
    };
    data.groups[groupIndex] = updated;
    saveDemoData(data);
    return updated;
  },
  deleteGroup: async (groupId: string): Promise<void> => {
    const data = loadDemoData();
    data.groups = data.groups.filter((item) => item.id !== groupId);
    data.members = data.members.filter((item) => item.groupId !== groupId);
    data.invitations = data.invitations.filter(
      (item) => item.groupId !== groupId
    );
    saveDemoData(data);
  },
  getMembers: async (groupId: string): Promise<GroupMember[]> => {
    const data = loadDemoData();
    return data.members.filter((member) => member.groupId === groupId);
  },
  addMember: async (
    groupId: string,
    payload: { userId: string; role: GroupRole }
  ): Promise<GroupMember> => {
    const data = loadDemoData();
    const now = new Date().toISOString();
    const member: GroupMember = {
      id: uuidv4(),
      groupId,
      userId: payload.userId,
      role: payload.role,
      status: "active",
      invitedBy: DEMO_USER_ID,
      invitedAt: now,
      acceptedAt: now,
      displayName: payload.userId,
    };
    data.members.push(member);
    saveDemoData(data);
    return member;
  },
  updateMember: async (
    groupId: string,
    userId: string,
    role: GroupRole
  ): Promise<GroupMember> => {
    const data = loadDemoData();
    const index = data.members.findIndex(
      (member) => member.groupId === groupId && member.userId === userId
    );
    if (index === -1) {
      throw new Error("Member not found");
    }
    data.members[index] = { ...data.members[index], role };
    saveDemoData(data);
    return data.members[index];
  },
  removeMember: async (groupId: string, userId: string): Promise<void> => {
    const data = loadDemoData();
    data.members = data.members.filter(
      (member) => !(member.groupId === groupId && member.userId === userId)
    );
    saveDemoData(data);
  },
  listInvitations: async (groupId?: string): Promise<GroupInvitation[]> => {
    const data = loadDemoData();
    if (groupId) {
      return data.invitations.filter((invite) => invite.groupId === groupId);
    }
    return data.invitations;
  },
  sendInvitation: async (
    groupId: string,
    payload: { email: string; role: Exclude<GroupRole, "owner"> }
  ): Promise<GroupInvitation> => {
    const data = loadDemoData();
    const invite: GroupInvitation = {
      id: uuidv4(),
      groupId,
      email: payload.email,
      role: payload.role,
      invitedBy: DEMO_USER_ID,
      invitedAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 1000 * 60 * 60 * 24 * 7).toISOString(),
    };
    data.invitations.push(invite);
    saveDemoData(data);
    return invite;
  },
  resendInvitation: async (inviteId: string): Promise<GroupInvitation> => {
    const data = loadDemoData();
    const index = data.invitations.findIndex(
      (invite) => invite.id === inviteId
    );
    if (index === -1) {
      throw new Error("Invitation not found");
    }
    const updated: GroupInvitation = {
      ...data.invitations[index],
      invitedAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 1000 * 60 * 60 * 24 * 7).toISOString(),
    };
    data.invitations[index] = updated;
    saveDemoData(data);
    return updated;
  },
  resolveInvitation: async (inviteId: string): Promise<void> => {
    const data = loadDemoData();
    data.invitations = data.invitations.filter(
      (invite) => invite.id !== inviteId
    );
    saveDemoData(data);
  },
  cancelInvitation: async (inviteId: string): Promise<void> => {
    const data = loadDemoData();
    data.invitations = data.invitations.filter(
      (invite) => invite.id !== inviteId
    );
    saveDemoData(data);
  },
  getSharesByGroup: async (groupId: string): Promise<RecordShare[]> => {
    const data = loadDemoData();
    return data.shares.filter((share) => share.groupId === groupId);
  },
  getSharesForRecord: async (
    recordType: ShareRecordType,
    recordId: string
  ): Promise<RecordShare[]> => {
    const data = loadDemoData();
    return data.shares.filter(
      (share) => share.recordType === recordType && share.recordId === recordId
    );
  },
  shareRecord: async (payload: {
    recordType: ShareRecordType;
    recordId: string;
    groupIds: string[];
    permission: SharePermission;
  }): Promise<RecordShare[]> => {
    const data = loadDemoData();
    const now = new Date().toISOString();
    const newShares = payload.groupIds.map((groupId) => ({
      id: uuidv4(),
      recordType: payload.recordType,
      recordId: payload.recordId,
      groupId,
      permission: payload.permission,
      sharedBy: DEMO_USER_ID,
      createdAt: now,
    }));
    data.shares.push(...newShares);
    saveDemoData(data);
    return newShares;
  },
  unshareRecord: async (shareId: string): Promise<void> => {
    const data = loadDemoData();
    data.shares = data.shares.filter((share) => share.id !== shareId);
    saveDemoData(data);
  },
};

export const groupAPI = {
  list: async (): Promise<Group[]> => {
    if (demoService.isDemo()) {
      return demoGroupsAPI.listGroups();
    }
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/groups`, { headers });
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to fetch groups");
    }
    const groups: Group[] = await response.json();

    // Fetch members for each group to populate memberNames
    const groupsWithMembers = await Promise.all(
      groups.map(async (group) => {
        try {
          const membersResponse = await fetch(
            `${API_URL}/groups/${group.id}/members`,
            { headers }
          );
          if (membersResponse.ok) {
            const members: GroupMember[] = await membersResponse.json();
            const activeMembers = members.filter((m) => m.status === "active");
            return {
              ...group,
              memberNames: activeMembers.map(
                (m) => m.displayName || m.email || "Unknown"
              ),
            };
          }
        } catch {
          // If fetching members fails, return group without memberNames
        }
        return group;
      })
    );

    return groupsWithMembers;
  },
  getById: async (groupId: string): Promise<Group | null> => {
    if (demoService.isDemo()) {
      return demoGroupsAPI.getGroup(groupId);
    }
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/groups/${groupId}`, { headers });
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to fetch group");
    }
    return response.json();
  },
  create: async (payload: {
    name: string;
    type: GroupType;
    description?: string;
  }): Promise<Group> => {
    if (demoService.isDemo()) {
      return demoGroupsAPI.createGroup(payload);
    }
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/groups`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to create group");
    }
    return response.json();
  },
  update: async (groupId: string, payload: Partial<Group>): Promise<Group> => {
    if (demoService.isDemo()) {
      return demoGroupsAPI.updateGroup(groupId, payload);
    }
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/groups/${groupId}`, {
      method: "PUT",
      headers,
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to update group");
    }
    return response.json();
  },
  remove: async (groupId: string): Promise<void> => {
    if (demoService.isDemo()) {
      return demoGroupsAPI.deleteGroup(groupId);
    }
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/groups/${groupId}`, {
      method: "DELETE",
      headers,
    });
    if (!response.ok) {
      let parsedErrorMessage: string | null = null;
      const responseClone = response.clone();
      try {
        const errorBody = await responseClone.json();
        if (errorBody?.error) {
          parsedErrorMessage = String(errorBody.error);
        }
      } catch {
        // Ignore parse issues and fall through.
      }
      if (parsedErrorMessage) {
        throw new Error(parsedErrorMessage);
      }
      await handleApiError(response);
      throw new Error("Failed to delete group");
    }
  },
  transferOwner: async (groupId: string, userId: string): Promise<void> => {
    if (demoService.isDemo()) {
      await demoGroupsAPI.updateGroup(groupId, {});
      return;
    }
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/groups/${groupId}/transfer`, {
      method: "POST",
      headers,
      body: JSON.stringify({ userId }),
    });
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to transfer ownership");
    }
  },
  members: {
    list: async (groupId: string): Promise<GroupMember[]> => {
      if (demoService.isDemo()) {
        return demoGroupsAPI.getMembers(groupId);
      }
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/groups/${groupId}/members`, {
        headers,
      });
      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to fetch members");
      }
      return response.json();
    },
    add: async (
      groupId: string,
      payload: { userId: string; role: GroupRole }
    ): Promise<GroupMember> => {
      if (demoService.isDemo()) {
        return demoGroupsAPI.addMember(groupId, payload);
      }
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/groups/${groupId}/members`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to add member");
      }
      return response.json();
    },
    update: async (
      groupId: string,
      userId: string,
      role: GroupRole
    ): Promise<GroupMember> => {
      if (demoService.isDemo()) {
        return demoGroupsAPI.updateMember(groupId, userId, role);
      }
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_URL}/groups/${groupId}/members/${userId}`,
        {
          method: "PUT",
          headers,
          body: JSON.stringify({ role }),
        }
      );
      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to update member");
      }
      return response.json();
    },
    remove: async (groupId: string, userId: string): Promise<void> => {
      if (demoService.isDemo()) {
        return demoGroupsAPI.removeMember(groupId, userId);
      }
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_URL}/groups/${groupId}/members/${userId}`,
        {
          method: "DELETE",
          headers,
        }
      );
      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to remove member");
      }
    },
    leave: async (groupId: string): Promise<void> => {
      if (demoService.isDemo()) {
        return demoGroupsAPI.removeMember(groupId, DEMO_USER_ID);
      }
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/groups/${groupId}/leave`, {
        method: "POST",
        headers,
      });
      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to leave group");
      }
    },
  },
  invitations: {
    listGroupInvites: async (groupId: string): Promise<GroupInvitation[]> => {
      if (demoService.isDemo()) {
        return demoGroupsAPI.listInvitations(groupId);
      }
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/groups/${groupId}/invite`, {
        headers,
      });
      if (response.status === 404) {
        return [];
      }
      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to fetch invitations");
      }
      return response.json();
    },
    listUserInvites: async (): Promise<GroupInvitation[]> => {
      if (demoService.isDemo()) {
        return demoGroupsAPI.listInvitations();
      }
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/invitations`, { headers });
      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to fetch invitations");
      }
      return response.json();
    },
    send: async (
      groupId: string,
      payload: { email: string; role: Exclude<GroupRole, "owner"> }
    ): Promise<GroupInvitation> => {
      if (demoService.isDemo()) {
        return demoGroupsAPI.sendInvitation(groupId, payload);
      }
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/groups/${groupId}/invite`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorBody = await response.text();
        console.error("Failed to send invitation", {
          status: response.status,
          statusText: response.statusText,
          errorBody,
          groupId,
          hasEmail: Boolean(payload.email),
          role: payload.role,
        });
        await handleApiError(response);
        throw new Error(errorBody || "Failed to send invitation");
      }
      return response.json();
    },
    cancel: async (groupId: string, inviteId: string): Promise<void> => {
      if (demoService.isDemo()) {
        return demoGroupsAPI.cancelInvitation(inviteId);
      }
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_URL}/groups/${groupId}/invite/${inviteId}`,
        {
          method: "DELETE",
          headers,
        }
      );
      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to cancel invitation");
      }
    },
    resend: async (
      groupId: string,
      inviteId: string
    ): Promise<GroupInvitation> => {
      if (demoService.isDemo()) {
        return demoGroupsAPI.resendInvitation(inviteId);
      }
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_URL}/groups/${groupId}/invite/${inviteId}/resend`,
        {
          method: "POST",
          headers,
        }
      );
      if (!response.ok) {
        const errorBody = await response.text();
        console.error("Failed to resend invitation", {
          status: response.status,
          statusText: response.statusText,
          errorBody,
        });
        await handleApiError(response);
        throw new Error(errorBody || "Failed to resend invitation");
      }
      return response.json();
    },
    accept: async (token: string): Promise<void> => {
      if (demoService.isDemo()) {
        return demoGroupsAPI.resolveInvitation(token);
      }
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/invitations/${token}/accept`, {
        method: "POST",
        headers,
      });
      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to accept invitation");
      }
    },
    decline: async (token: string): Promise<void> => {
      if (demoService.isDemo()) {
        return demoGroupsAPI.resolveInvitation(token);
      }
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/invitations/${token}/decline`, {
        method: "POST",
        headers,
      });
      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to decline invitation");
      }
    },
  },
  users: {
    search: async (
      query: string
    ): Promise<{ id: string; displayName: string; email: string }[]> => {
      if (demoService.isDemo()) {
        return [
          { id: "demo-alex", displayName: "Alex", email: "alex@demo.local" },
          {
            id: "demo-morgan",
            displayName: "Morgan",
            email: "morgan@demo.local",
          },
        ].filter((user) =>
          user.displayName.toLowerCase().includes(query.toLowerCase())
        );
      }
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_URL}/users/search?q=${encodeURIComponent(query)}`,
        { headers }
      );
      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to search users");
      }
      return response.json();
    },
  },
};

export const shareAPI = {
  shareRecord: async (payload: {
    recordType: ShareRecordType;
    recordId: string;
    groupIds: string[];
    permission: SharePermission;
  }): Promise<RecordShare[]> => {
    if (demoService.isDemo()) {
      return demoGroupsAPI.shareRecord(payload);
    }
    console.log("Sharing record", payload);
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/shares`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to share record");
    }
    const data = await response.json();
    console.log("Share record response", data);
    return data;
  },
  getSharesForRecord: async (
    recordType: ShareRecordType,
    recordId: string
  ): Promise<RecordShare[]> => {
    if (demoService.isDemo()) {
      return demoGroupsAPI.getSharesForRecord(recordType, recordId);
    }
    console.log("Fetching shares for record", { recordType, recordId });
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_URL}/shares/record/${recordType}/${recordId}`,
      { headers }
    );
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to fetch shares");
    }
    const data = await response.json();
    console.log("Shares for record response", data);
    return data;
  },
  getSharesByGroup: async (groupId: string): Promise<RecordShare[]> => {
    if (demoService.isDemo()) {
      return demoGroupsAPI.getSharesByGroup(groupId);
    }
    console.log("Fetching shares for group", { groupId });
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/groups/${groupId}/shared`, {
      headers,
    });
    if (response.status === 404) {
      return [];
    }
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to fetch shared records");
    }
    const data = await response.json();
    console.log("Shares for group response", data);
    return data;
  },
  shareAllByType: async (payload: {
    groupId: string;
    recordType: ShareRecordType;
    permission: SharePermission;
  }): Promise<{ success: true; totalFound: number }> => {
    if (demoService.isDemo()) {
      return { success: true, totalFound: 0 };
    }

    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_URL}/groups/${payload.groupId}/share-all`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({
          recordType: payload.recordType,
          permission: payload.permission,
        }),
      }
    );
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to share all records");
    }
    return response.json();
  },
  unshareAllByType: async (payload: {
    groupId: string;
    recordType: ShareRecordType;
  }): Promise<{ success: true }> => {
    if (demoService.isDemo()) {
      return { success: true };
    }
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_URL}/groups/${payload.groupId}/share-all`,
      {
        method: "DELETE",
        headers,
        body: JSON.stringify({
          recordType: payload.recordType,
        }),
      }
    );
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to unshare all records");
    }
    return response.json();
  },
  getShareAllStatus: async (
    groupId: string
  ): Promise<Partial<Record<ShareRecordType, boolean>>> => {
    if (demoService.isDemo()) {
      return {};
    }
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_URL}/groups/${groupId}/share-all-status`,
      {
        headers,
      }
    );
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to fetch share-all status");
    }
    return response.json();
  },
  unshare: async (shareId: string): Promise<void> => {
    if (demoService.isDemo()) {
      return demoGroupsAPI.unshareRecord(shareId);
    }
    console.log("Unsharing record", { shareId });
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/shares/${shareId}`, {
      method: "DELETE",
      headers,
    });
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to unshare record");
    }
    console.log("Unshare record success", { shareId });
  },
};

export const deletedRecordsAPI = {
  list: async (): Promise<DeletedRecord[]> => {
    if (demoService.isDemo()) {
      return [];
    }
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/deleted-records`, { headers });
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to fetch deleted records");
    }
    const records = await response.json();
    return records.map((record: any) => ({
      id: record.id,
      recordType: record.record_type,
      recordId: record.record_id,
      recordMetadata: record.record_metadata || {},
      groupId: record.group_id,
      deletedBy: record.deleted_by,
      deletedAt: record.deleted_at,
      restoredAt: record.restored_at,
      restoredBy: record.restored_by,
      expiresAt: record.expires_at,
    }));
  },
  restore: async (recordId: string): Promise<void> => {
    if (demoService.isDemo()) {
      return;
    }
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_URL}/deleted-records/${recordId}/restore`,
      {
        method: "POST",
        headers,
      }
    );
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to restore deleted record");
    }
  },
};
